import { useAtomValue } from "jotai";
import { useRef, useState } from "react";
import { Popover, Whisper } from "rsuite";
import { OverlayTriggerHandle } from "rsuite/esm/internals/Overlay/OverlayTrigger";

import { tenantAtom } from "../../atoms/GlobalAtoms";
import useWorkspaces from "../../api/api-hooks/workspaces/useWorkspaces";
import { workspaceIdAtom } from "../../atoms/WorkspaceAtoms";
import { WorkspaceContextLoadingAtom } from "../../context/WorkspaceContext";
import CloudSwitcherDrawer from "../Drawers/CloudSwitcherDrawer";
import useToggle from "../../hooks/useToggle";
import WorkspaceSwitcherOptions from "./WorkspaceSwitcher/WorkspaceSwitcherOptions";
import EditWorkspaceDrawer from "../Drawers/EditWorkspaceDrawer/EditWorkspaceDrawer";
import SidebarToggler from "./SidebarToggler";
import SidebarSwitcher from "./SidebarSwitcher";
import useWorkspace from "../../api/api-hooks/workspaces/useWorkspace";

interface SidebarHeaderProps {
  open: boolean;
  toggleOpen: () => void;
  title?: string;
}

const SidebarHeader = ({
  open,
  toggleOpen,
  title = "",
}: SidebarHeaderProps) => {
  const [cloudDrawer, toggleCloudDrawer] = useToggle();
  const currentTenant = useAtomValue(tenantAtom);
  const { workspace, workspaceLoading: loading, enabled } = useWorkspace();
  const { workspacesLoading } = useWorkspaces();
  const workspaceID = useAtomValue(workspaceIdAtom);
  const contextLoading = useAtomValue(WorkspaceContextLoadingAtom);
  const wsRef = useRef<OverlayTriggerHandle>(null);
  const [workspaceDrawer, setWorkspaceDrawer] = useState(false);

  const workspaceLoading =
    workspacesLoading ||
    contextLoading ||
    (loading && enabled) ||
    (workspaceID && !workspace?.Name);

  return (
    <>
      <div className="tw:flex-col tw:px-3">
        <SidebarToggler toggleOpen={toggleOpen} open={open} />
        <SidebarSwitcher
          open={open}
          css="tw:hover:bg-primary-light-bg tw:border-primary tw:text-primary"
          icon="mdi mdi-cloud"
          border={false}
          text={open && (currentTenant?.TenantName ?? "Loading...")}
          rightIcon="mdi mdi-swap-horizontal"
          onClick={toggleCloudDrawer}
        />
        <Whisper
          placement="autoVerticalStart"
          controlId="workspace-switcher"
          trigger="click"
          ref={wsRef}
          speaker={
            <Popover
              className="sidebar-popover"
              full
              onClick={(e) => e.stopPropagation()}
            >
              <WorkspaceSwitcherOptions
                toggleModal={() => setWorkspaceDrawer(true)}
                closePopover={() => wsRef.current?.close()}
              />
            </Popover>
          }
        >
          <SidebarSwitcher
            open={open}
            icon="mdi mdi-view-dashboard-outline"
            rightIcon="mdi mdi-chevron-down"
            css="tw:hover:bg-sidebar-link tw:border-lighter tw:text-medium"
            text={
              workspaceLoading
                ? "Loading..."
                : title
                ? title
                : workspace?.Name
                ? workspace?.Name
                : "Select A Workspace"
            }
          />
        </Whisper>
      </div>
      <CloudSwitcherDrawer open={cloudDrawer} onClose={toggleCloudDrawer} />
      <EditWorkspaceDrawer
        open={workspaceDrawer}
        handleClose={() => setWorkspaceDrawer(false)}
      />
    </>
  );
};

export default SidebarHeader;
